<script setup lang="ts">
const props = defineProps({
  ingredient: {
    type: Object as PropType<IngredientCopies>,
    required: true,
  },
  currentStep: {
    type: Number,
    required: true,
  },
  totalSteps: {
    type: Number,
    required: true,
  },
  selectedIngredients: {
    type: Object as PropType<Record<string, string[]>>,
    required: true,
  },
});

const emit = defineEmits(["select", "next", "back", "close"]);
const selectedCopies = ref<string[]>([]);

watch(
  () => props.ingredient,
  (newIngredient) => {
    const previouslySelected = props.selectedIngredients[newIngredient.itemId];
    if (previouslySelected) {
      selectedCopies.value = [...previouslySelected];
    } else {
      selectedCopies.value = [];
    }
  },
  { immediate: true }
);

const toggleCopy = (copyId: string) => {
  const index = selectedCopies.value.indexOf(copyId);
  if (
    index === -1 &&
    selectedCopies.value.length < props.ingredient.requiredQuantity
  ) {
    selectedCopies.value.push(copyId);
  } else if (index !== -1) {
    selectedCopies.value.splice(index, 1);
  }
};

const handleNext = () => {
  emit("select", props.ingredient.itemId, selectedCopies.value);
  emit("next");
};
</script>

<template>
  <div class="space-y-4">
    <p class="text-primary-800 text-lg">
      {{ $t("main.modalCraftIngredient.selectCollectibles") }}
    </p>

    <div class="flex flex-col gap-2 max-h-96 overflow-y-auto pr-4">
      <div
        v-for="copy in ingredient.collectibles"
        :key="copy.id"
        class="relative cursor-pointer px-2 py-3 flex items-center gap-2 bg-primary-900 rounded-sm transition-all duration-300 border-2"
        :class="{
          'opacity-50':
            selectedCopies.length >= ingredient.requiredQuantity &&
            !selectedCopies.includes(copy.id),
          'border-primary-500': selectedCopies.includes(copy.id),
          'border-primary-900': !selectedCopies.includes(copy.id),
        }"
        @click="toggleCopy(copy.id)">
        <div class="relative">
          <img
            :src="getPortraitItemImage(ingredient.imageId)"
            alt=""
            class="w-10 h-10" />
          <div
            class="absolute bottom-0 right-0 w-full"
            :class="`border-b-2 ${getRarityColor(
              ingredient.rarity,
              'border'
            )}`" />
        </div>
        <p class="text-primary-800 text-xs font-bold grow truncate">
          {{ $t(`${ingredient.translationId}.title`) }}
          {{ $t(`${ingredient.translationId}.subtitle`) }}
        </p>
        <div
          class="bg-neutral-500 border border-neutral-400 text-neutral-200 font-bold text-center w-16 h-8 py-1 rounded-lg flex items-center justify-center">
          #{{ copy.number }}
        </div>
        <div
          v-if="copy.level"
          class="text-2xl text-neutral-200 font-bold text-center w-fit rounded-lg flex items-center justify-center">
          <BaseIconHexagon class="w-10 h-10" />
          <p class="absolute z-10">{{ copy.level }}</p>
        </div>
        <div class="w-6 h-6 flex items-center justify-center">
          <Transition name="fade">
            <NuxtIcon
              v-if="selectedCopies.includes(copy.id)"
              name="mdi:check"
              class="justify-self-end w-6 h-6 text-primary-500" />
          </Transition>
        </div>
      </div>
    </div>
    <div class="flex justify-between pr-4">
      <p class="text-primary-500">
        {{
          $t("main.modalCraftIngredient.selectedCollectibles", {
            selected: selectedCopies.length,
            total: ingredient.requiredQuantity,
          })
        }}
      </p>
      <p class="text-neutral-400 font-bold">
        {{
          $t("main.modalCraftIngredient.step", {
            current: currentStep,
            total: totalSteps,
          })
        }}
      </p>
    </div>

    <div class="flex justify-end gap-4">
      <BaseButtonCancel
        v-if="currentStep > 1"
        @click="emit('back')"
        :text="$t('main.modalCraftIngredient.back')"
        :contextual-styles="true" />
      <BaseButtonCancel
        v-else
        @click="emit('close')"
        :text="$t('main.modalCraftIngredient.cancel')" />
      <BaseButtonSubmit
        :disabled="selectedCopies.length !== ingredient.requiredQuantity"
        class="w-40"
        @click="handleNext"
        :text="
          currentStep === totalSteps
            ? $t('main.modalCraftIngredient.confirm')
            : $t('main.modalCraftIngredient.nextStep')
        "
        :contextual-styles="true" />
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
